<template>
  <div class="fs-page py-3">
    <header class="fs-toolbar">
      <div class="fs-toolbar__title">
        <span class="fs-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Field Sales Form</h1>
          <div class="fs-toolbar__subtitle">Capture a field visit, mark intent, and create an order on the spot when the customer buys.</div>
        </div>
      </div>
      <div class="fs-toolbar__meta">
        <CButton color="secondary" size="sm" variant="outline" :disabled="isLoading" @click="loadBootstrapData">
          {{ isLoading ? 'Loading…' : 'Refresh' }}
        </CButton>
        <CButton color="danger" size="sm" variant="outline" :disabled="isSubmitting" @click="resetForm">Reset</CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="fs-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="fs-alert">{{ successMessage }}</CAlert>

    <CRow class="fs-grid">
      <CCol lg="8" class="fs-main">

        <section class="fs-section">
          <header class="fs-section__head">
            <h2><span class="step-num">1</span> Location</h2>
            <span class="fs-section__hint">Capture GPS — append more as you move</span>
          </header>
          <div class="fs-section__body">
            <div class="fs-location-bar">
              <div class="fs-location-bar__hint">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span v-if="!locations.length">No location captured yet</span>
                <span v-else>{{ locations.length }} location{{ locations.length === 1 ? '' : 's' }} captured</span>
              </div>
              <button type="button" class="fs-add-loc" :disabled="isCapturingLocation" @click="captureLocation">
                <span v-if="isCapturingLocation" class="fs-add-loc__spinner" aria-hidden="true"></span>
                <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {{ isCapturingLocation ? 'Locating…' : 'Add Location' }}
              </button>
            </div>

            <div v-if="locationError" class="fs-location-error" role="alert">{{ locationError }}</div>

            <ul v-if="locations.length" class="fs-location-list">
              <li v-for="(loc, idx) in locations" :key="`loc-${idx}`" class="fs-location-list__row">
                <div class="fs-location-list__main">
                  <div class="fs-location-list__coords">
                    <span class="fs-location-list__index">#{{ idx + 1 }}</span>
                    <span class="fs-location-list__latlng">{{ formatCoord(loc.lat) }}, {{ formatCoord(loc.lng) }}</span>
                  </div>
                  <div class="fs-location-list__meta">
                    {{ formatTime(loc.capturedAt) }}<span v-if="loc.accuracy"> · ±{{ Math.round(loc.accuracy) }}m</span>
                  </div>
                </div>
                <a class="fs-location-list__map" :href="mapUrl(loc)" target="_blank" rel="noopener">Map</a>
                <button type="button" class="fs-location-list__remove" aria-label="Remove location" @click="removeLocation(idx)">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </li>
            </ul>
            <div v-else class="fs-empty">Tap <strong>Add Location</strong> to capture the current GPS coordinates. You can capture more than one — handy if the visit covers multiple plots.</div>
          </div>
        </section>

        <section class="fs-section">
          <header class="fs-section__head">
            <h2><span class="step-num">2</span> Customer Details</h2>
          </header>
          <div class="fs-section__body">
            <CRow>
              <CCol md="6">
                <AppInput label="Full Name" required placeholder="Customer name" :value="form.name" @update:value="form.name = $event" />
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Phone Number"
                  required
                  type="tel"
                  placeholder="10-digit phone number"
                  :maxlength="10"
                  :value="form.phone"
                  @update:value="setPhone($event)"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12">
                <AppInput label="Address" multiline :rows="2" placeholder="House / street / landmark" :value="form.address" @update:value="form.address = $event" />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="4">
                <AppInput label="Pincode" placeholder="6-digit pincode" :maxlength="6" :value="form.pincode" @update:value="onPincodeInput($event)" />
              </CCol>
              <CCol md="8" class="form-group">
                <label class="fs-label">
                  Post Office
                  <span v-if="isPincodeLoading" class="fs-label__status">Looking up…</span>
                  <span v-else-if="form.pincode.length === 6 && !postOfficeOptions.length" class="fs-label__status fs-label__status--warn">No matches</span>
                </label>
                <AppDropdown
                  :value="form.post_office_name"
                  :options="postOfficeDropdownOptions"
                  :placeholder="postOfficeOptions.length ? 'Select a post office' : 'Enter pincode to auto-fill'"
                  @update:value="onPostOfficeSelect($event)"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="6">
                <AppInput label="District" :value="form.district" disabled />
              </CCol>
              <CCol md="6">
                <AppInput label="State" :value="form.state" disabled />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12" class="form-group">
                <label class="fs-label">Local Body</label>
                <AppDropdown
                  :value="selectedLocalBodyName"
                  :options="localBodyDropdownOptions"
                  :placeholder="localBodyOptions.length ? 'Search and select a local body' : 'Auto-loads after pincode'"
                  searchable
                  search-placeholder="Search local body…"
                  @update:value="onLocalBodySelect($event)"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12">
                <AppInput label="Type of Agriculture" placeholder="e.g. Paddy, Rubber, Mixed crop, Dairy" :value="form.agro_type" @update:value="form.agro_type = $event" />
              </CCol>
            </CRow>
          </div>
        </section>

        <section class="fs-section">
          <header class="fs-section__head">
            <h2><span class="step-num">3</span> Visit Status</h2>
            <span class="fs-section__hint">How did this visit end?</span>
          </header>
          <div class="fs-section__body">
            <div class="fs-status-grid" role="radiogroup" aria-label="Visit status">
              <button
                v-for="opt in visitStatusOptions"
                :key="opt.value"
                type="button"
                role="radio"
                :aria-checked="form.visit_status === opt.value"
                :class="['fs-status-card', `fs-status-card--${opt.tone}`, { 'is-active': form.visit_status === opt.value }]"
                @click="selectVisitStatus(opt.value)"
              >
                <span class="fs-status-card__dot" aria-hidden="true"></span>
                <span class="fs-status-card__label">{{ opt.label }}</span>
                <span class="fs-status-card__hint">{{ opt.hint }}</span>
              </button>
            </div>
            <CRow class="mt-3">
              <CCol md="12">
                <AppInput label="Comments" multiline :rows="2" placeholder="Anything worth noting from this visit?" :value="form.comments" @update:value="form.comments = $event" />
              </CCol>
            </CRow>
          </div>
        </section>

        <section v-if="isSold" class="fs-section">
          <header class="fs-section__head">
            <h2><span class="step-num">4</span> Order Items</h2>
            <span class="fs-section__hint">Customer is buying — add the products</span>
          </header>
          <div class="fs-section__body">
            <CRow>
              <CCol md="12" class="form-group">
                <label class="fs-label">Search Product</label>
                <AppDropdown :value="productDraft.item_id" :options="itemOptions" placeholder="Select a product" searchable search-placeholder="Search product…" @update:value="productDraft.item_id = $event" />
              </CCol>
            </CRow>

            <div class="fs-product-add">
              <div class="fs-product-add__field">
                <label class="fs-label">Price</label>
                <AppInput no-margin :value="productDraftPriceLabel" disabled />
              </div>
              <div class="fs-product-add__field fs-product-add__field--qty">
                <label class="fs-label">Qty</label>
                <AppInput no-margin type="number" :value="productDraft.quantity" @update:value="productDraft.quantity = Number($event || 0)" />
              </div>
              <div class="fs-product-add__field">
                <label class="fs-label">Total</label>
                <AppInput no-margin :value="productDraftTotalLabel" disabled />
              </div>
              <div class="fs-product-add__action">
                <CButton color="primary" class="w-100" :disabled="!productDraft.item_id || Number(productDraft.quantity || 0) <= 0" @click="addSelectedProduct">+ Add</CButton>
              </div>
            </div>

            <div v-if="!form.order_items.length" class="fs-empty">No items added yet. Pick a product above to start the order.</div>

            <ul v-else class="fs-item-list">
              <li v-for="(item, index) in form.order_items" :key="`item-${index}`" class="fs-item-list__row">
                <div class="fs-item-list__main">
                  <div class="fs-item-list__name">{{ item.name }}</div>
                  <div class="fs-item-list__meta">{{ formatCurrency(item.price) }} each</div>
                </div>
                <div class="fs-item-list__qty">
                  <label class="fs-label-inline">Qty</label>
                  <AppInput no-margin type="number" :value="item.quantity" @update:value="updateItemQuantity(index, $event)" />
                </div>
                <div class="fs-item-list__total">
                  <span class="fs-label-inline">Total</span>
                  <strong>{{ formatCurrency(item.total) }}</strong>
                </div>
                <CButton color="danger" variant="outline" size="sm" class="fs-item-list__remove" @click="removeItem(index)">Remove</CButton>
              </li>
            </ul>

            <CRow v-if="form.order_items.length" class="mt-3">
              <CCol md="6" class="form-group">
                <label class="fs-label">Discount</label>
                <div class="fs-discount-row">
                  <AppDropdown class="fs-discount-row__mode" :value="discountMode" :options="discountModeOptions" @update:value="discountMode = $event" />
                  <AppInput no-margin type="number" :value="discountInput" @update:value="onDiscountInput($event)" />
                </div>
                <small class="text-muted">After discount: {{ formatCurrency(totalAfterDiscount) }}</small>
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Shipping Charge (₹)"
                  type="number"
                  :value="form.shipping_charge"
                  @update:value="onShippingChargeInput($event)"
                  class="mb-0"
                />
              </CCol>
            </CRow>
          </div>
        </section>

        <section v-if="isSold" class="fs-section">
          <header class="fs-section__head">
            <h2><span class="step-num">5</span> Payment &amp; Shipping</h2>
          </header>
          <div class="fs-section__body">
            <CRow>
              <CCol md="6" class="form-group">
                <label class="fs-label">Payment Method<span class="fs-label__required" aria-hidden="true">*</span></label>
                <AppDropdown :value="form.payment_method" :options="paymentMethodOptions" placeholder="Select payment method" @update:value="onPaymentMethodChange" />
              </CCol>
              <CCol md="6" class="form-group">
                <label class="fs-label">Shipping Method<span class="fs-label__required" aria-hidden="true">*</span></label>
                <AppDropdown :value="form.shipping_method" :options="shippingMethodOptions" placeholder="Select shipping method" @update:value="form.shipping_method = $event" />
              </CCol>
            </CRow>
            <div class="fs-amount-due">
              <div class="fs-amount-due__body">
                <span class="fs-amount-due__label">Amount To Collect</span>
                <span class="fs-amount-due__value">{{ formatCurrency(form.grand_total) }}</span>
              </div>
              <span class="fs-amount-due__hint">Includes GST &amp; shipping</span>
            </div>
          </div>
        </section>
      </CCol>

      <CCol lg="4" class="fs-aside">
        <div class="fs-sticky">
          <section class="fs-section fs-summary-section">
            <header class="fs-section__head">
              <h2>{{ isSold ? 'Visit + Order Summary' : 'Visit Summary' }}</h2>
            </header>
            <div class="fs-section__body">
              <div class="fs-summary-line"><span>Visit Status</span><strong>{{ visitStatusLabel || '—' }}</strong></div>
              <div class="fs-summary-line"><span>Customer</span><strong>{{ form.name || '—' }}</strong></div>
              <div class="fs-summary-line"><span>Phone</span><strong>{{ form.phone || '—' }}</strong></div>
              <div class="fs-summary-line"><span>Locations</span><strong>{{ locations.length }}</strong></div>

              <template v-if="isSold">
                <div class="fs-summary-divider"></div>
                <div class="fs-summary-line"><span>Items</span><strong>{{ totalQuantity }}</strong></div>
                <div class="fs-summary-line"><span>Subtotal (incl. GST)</span><strong>{{ formatCurrency(form.total) }}</strong></div>
                <div class="fs-summary-line"><span>Discount</span><strong>−{{ formatCurrency(form.discount) }}</strong></div>
                <div class="fs-summary-line"><span>Shipping</span><strong>{{ formatCurrency(form.shipping_charge) }}</strong></div>
                <div v-if="form.cod_fee > 0" class="fs-summary-line"><span>COD Fee</span><strong>{{ formatCurrency(form.cod_fee) }}</strong></div>
                <div class="fs-summary-line fs-summary-line--total"><span>Grand Total</span><strong>{{ formatCurrency(form.grand_total) }}</strong></div>
                <div class="fs-summary-line fs-summary-line--note"><span>Includes {{ gstSummaryLabel }}</span><strong>{{ formatCurrency(form.gst_amt) }}</strong></div>
              </template>

              <div class="fs-summary-actions">
                <CButton
                  color="success"
                  class="w-100 fs-summary-actions__primary"
                  :disabled="isSubmitting || !isFormReady"
                  @click="submitVisit"
                >
                  {{ submitButtonLabel }}
                </CButton>
                <CButton color="danger" variant="outline" size="sm" class="w-100" :disabled="isSubmitting" @click="resetForm">Reset</CButton>
                <small v-if="!isFormReady" class="fs-summary-actions__hint">{{ readinessHint }}</small>
              </div>
            </div>
          </section>
        </div>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'

const VISIT_STATUSES = [
  { value: 'sold', label: 'Sold', tone: 'green', hint: 'Customer bought — proceed to checkout' },
  { value: 'interested', label: 'Interested', tone: 'blue', hint: 'Likely to convert' },
  { value: 'interested_later', label: 'Interested Later', tone: 'amber', hint: 'Follow up at a later date' },
  { value: 'agency', label: 'Agency', tone: 'violet', hint: 'B2B / dealer lead' }
]

export default {
  name: 'FieldSalesForm',
  components: { AppDropdown, AppInput },
  data () {
    return {
      isLoading: false,
      isSubmitting: false,
      isPincodeLoading: false,
      isCapturingLocation: false,
      errorMessage: '',
      successMessage: '',
      locationError: '',
      locations: [],
      postOfficeRecords: [],
      postOfficeOptions: [],
      localBodyOptions: [],
      selectedLocalBodyName: '',
      itemOptions: [],
      paymentMethodOptions: [],
      shippingMethodOptions: [],
      productDraft: {
        item_id: '',
        quantity: 1
      },
      discountMode: 'amount',
      discountInput: 0,
      discountModeOptions: [
        { value: 'amount', label: '₹' },
        { value: 'percent', label: '%' }
      ],
      visitStatusOptions: VISIT_STATUSES,
      form: {
        name: '',
        phone: '',
        address: '',
        pincode: '',
        post_office_name: '',
        district: '',
        state: '',
        local_body_name: '',
        local_body_type_name: '',
        agro_type: '',
        visit_status: '',
        comments: '',
        payment_method: '',
        shipping_method: '',
        order_items: [],
        discount: 0,
        shipping_charge: 0,
        cod_fee: 0,
        total: 0,
        grand_total: 0,
        gst_percentage: 5,
        gst_amt: 0,
        gst_state_code: '',
        cgst_amt: 0,
        sgst_amt: 0,
        igst_amt: 0
      }
    }
  },
  computed: {
    activeOrganisationId () {
      return Number(this.$store?.state?.activeOrganisationId || 0)
    },
    isSold () {
      return this.form.visit_status === 'sold'
    },
    visitStatusLabel () {
      const found = this.visitStatusOptions.find((o) => o.value === this.form.visit_status)
      return found ? found.label : ''
    },
    postOfficeDropdownOptions () {
      return this.postOfficeOptions.map((name) => ({ value: name, label: name }))
    },
    localBodyDropdownOptions () {
      return this.localBodyOptions.map((body) => ({
        value: body.local_body_name,
        label: body.local_body_name
      }))
    },
    productDraftItem () {
      return this.itemOptions.find((option) => option.value === String(this.productDraft.item_id)) || null
    },
    productDraftPrice () {
      return Number(this.productDraftItem?.price || 0)
    },
    productDraftPriceLabel () {
      return this.formatCurrency(this.productDraftPrice)
    },
    productDraftTotalLabel () {
      return this.formatCurrency(Number(this.productDraftPrice) * Number(this.productDraft.quantity || 0))
    },
    totalQuantity () {
      return this.form.order_items.reduce((acc, item) => acc + Number(item.quantity || 0), 0)
    },
    // Sum of qty × per-item shipping weight (g). Posted to cal_cod_charges
    // so the backend uses real catalogue weights for slab selection rather
    // than the legacy 200 g / 700 g flat per item, which under-charged any
    // heavy SKU. Falls back to the per-org flat only when neither weight
    // is populated on the catalogue.
    orderTotalWeightGrams () {
      const fallback = this.activeOrganisationId === 2 ? 700 : 200
      const byId = new Map(this.itemOptions.map((opt) => [String(opt.value), opt]))
      return this.form.order_items.reduce((sum, line) => {
        const opt = byId.get(String(line.item_id))
        const perItem = Number(opt?.shipping_weight || opt?.weight || 0) || fallback
        return sum + (Number(line.quantity || 0) * perItem)
      }, 0)
    },
    totalAfterDiscount () {
      return Number(this.form.total || 0) - Number(this.form.discount || 0)
    },
    gstSummaryLabel () {
      return this.form.gst_state_code === 'KL'
        ? `CGST + SGST (${this.form.gst_percentage}%)`
        : `IGST (${this.form.gst_percentage}%)`
    },
    submitButtonLabel () {
      if (this.isSubmitting) return this.isSold ? 'Creating Order…' : 'Saving Visit…'
      if (this.isSold) return 'Save Visit & Create Order'
      if (this.form.visit_status) return 'Save Visit'
      return 'Save Visit'
    },
    isFormReady () {
      if (!this.locations.length) return false
      if (!this.form.name) return false
      if (!this.form.phone || this.form.phone.length !== 10) return false
      if (!this.form.visit_status) return false
      if (this.isSold) {
        const hasValidItems = this.form.order_items.some((item) => item.item_id && Number(item.quantity || 0) > 0)
        if (!hasValidItems) return false
        if (!this.form.payment_method) return false
        if (!this.form.shipping_method) return false
      }
      return true
    },
    readinessHint () {
      if (!this.locations.length) return 'Capture at least one location to enable saving.'
      if (!this.form.name) return 'Enter the customer name.'
      if (!this.form.phone || this.form.phone.length !== 10) return 'Enter a valid 10-digit phone number.'
      if (!this.form.visit_status) return 'Select a visit status.'
      if (this.isSold) {
        const hasValidItems = this.form.order_items.some((item) => item.item_id && Number(item.quantity || 0) > 0)
        if (!hasValidItems) return 'Add at least one product to the order.'
        if (!this.form.payment_method) return 'Select a payment method.'
        if (!this.form.shipping_method) return 'Select a shipping method.'
      }
      return ''
    }
  },
  created () {
    this.loadBootstrapData()
  },
  methods: {
    getInputValue (input) {
      return input && typeof input === 'object' && input.target ? input.target.value : input
    },
    formatCurrency (value) {
      const numeric = Number(value || 0)
      return Number.isFinite(numeric) ? `₹${numeric.toFixed(2)}` : '₹0.00'
    },
    formatCoord (value) {
      const numeric = Number(value || 0)
      return Number.isFinite(numeric) ? numeric.toFixed(6) : '—'
    },
    formatTime (iso) {
      if (!iso) return ''
      try {
        return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } catch (e) {
        return ''
      }
    },
    mapUrl (loc) {
      const lat = Number(loc.lat)
      const lng = Number(loc.lng)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '#'
      return `https://www.google.com/maps?q=${lat},${lng}`
    },
    buildDropdown (entries) {
      return (entries || []).map((entry) => ({
        value: entry.slug || entry.value || entry.id || '',
        label: entry.name || entry.text || entry.slug || ''
      })).filter((entry) => entry.value || entry.label)
    },
    normalizeResponseArray (value) {
      return Array.isArray(value) ? value : []
    },
    captureLocation () {
      this.locationError = ''
      if (!('geolocation' in navigator)) {
        this.locationError = 'Geolocation is not supported on this device.'
        return
      }
      this.isCapturingLocation = true
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.locations.push({
            lat: Number(position.coords.latitude),
            lng: Number(position.coords.longitude),
            accuracy: Number(position.coords.accuracy || 0),
            capturedAt: new Date().toISOString()
          })
          this.isCapturingLocation = false
        },
        (error) => {
          this.isCapturingLocation = false
          if (error && error.code === 1) {
            this.locationError = 'Permission denied. Allow location access in your browser settings, then try again.'
          } else if (error && error.code === 2) {
            this.locationError = 'Could not determine location. Check that GPS is enabled.'
          } else if (error && error.code === 3) {
            this.locationError = 'Location request timed out. Try again.'
          } else {
            this.locationError = 'Unable to capture location.'
          }
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      )
    },
    removeLocation (idx) {
      this.locations.splice(idx, 1)
    },
    setPhone (value) {
      this.form.phone = String(value || '').replace(/\D/g, '').slice(0, 10)
    },
    selectVisitStatus (status) {
      this.form.visit_status = status
      if (status !== 'sold') {
        this.form.payment_method = ''
        this.form.shipping_method = ''
        this.form.order_items = []
        this.form.discount = 0
        this.form.shipping_charge = 0
        this.form.cod_fee = 0
        this.discountMode = 'amount'
        this.discountInput = 0
        this.calculateTotals()
      } else {
        this.loadChargeEstimate()
      }
    },
    loadBootstrapData () {
      this.errorMessage = ''
      this.isLoading = true
      const orgId = Number(this.$store?.state?.activeOrganisationId) || null
      const params = orgId ? { organisation_id: orgId } : {}
      axios.get(`${this.$apiAdress}/api/order_form_data`, { params })
        .then((response) => {
          const data = response.data || {}
          this.paymentMethodOptions = this.buildDropdown(data.drop_down_items?.payment_method)
          this.shippingMethodOptions = this.buildDropdown(data.drop_down_items?.shipping_method)
            .filter((opt) => opt.value !== 'ip_registered_parcel')
          this.itemOptions = this.normalizeResponseArray(data.items).map((item) => ({
            value: String(item.id),
            label: item.name,
            sku: item.sku || '',
            shortcode: item.shortcode || '',
            price: Number(item.selling_price ?? item.price ?? item.rate ?? item.sale_price ?? item.unit_price ?? item.mrp ?? 0),
            weight: Number(item.weight || 0),
            shipping_weight: Number(item.shipping_weight || 0)
          }))
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load form data. Pull to refresh.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onPincodeInput (value) {
      const next = String(this.getInputValue(value) || '').replace(/\D/g, '').slice(0, 6)
      const changed = next !== this.form.pincode
      this.form.pincode = next
      if (next.length < 6) {
        this.postOfficeRecords = []
        this.postOfficeOptions = []
        return
      }
      if (changed && !this.isPincodeLoading) {
        this.searchPincode(true)
      }
    },
    searchPincode (loadLocalBodies = true) {
      if (this.form.pincode.length !== 6) return
      this.isPincodeLoading = true
      axios.get(`${this.$apiAdress}/api/pincodes/${this.form.pincode}`, { timeout: 15000 })
        .then((response) => {
          const results = Array.isArray(response.data) ? response.data : []
          this.postOfficeRecords = results
          this.postOfficeOptions = [...new Set(results.map((row) => row.OfficeName).filter(Boolean))]
          if (this.postOfficeOptions.length > 0 && !this.postOfficeOptions.includes(this.form.post_office_name)) {
            this.form.post_office_name = this.postOfficeOptions[0]
          }
          this.applyPostOfficeDetails()
          if (loadLocalBodies) this.loadDistrictLocalBodies()
          if (this.isSold) this.loadChargeEstimate()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Pincode lookup failed.'
          this.postOfficeRecords = []
          this.postOfficeOptions = []
        })
        .finally(() => {
          this.isPincodeLoading = false
        })
    },
    onPostOfficeSelect (value) {
      this.form.post_office_name = value
      this.applyPostOfficeDetails()
      this.loadDistrictLocalBodies()
      if (this.isSold) this.loadChargeEstimate()
    },
    applyPostOfficeDetails () {
      if (!this.postOfficeRecords.length) return
      const selected = this.postOfficeRecords.find((row) => row.OfficeName === this.form.post_office_name) || this.postOfficeRecords[0]
      this.form.post_office_name = selected?.OfficeName || ''
      this.form.pincode = String(selected?.Pincode || this.form.pincode || '')
      this.form.district = selected?.District || this.form.district
      this.form.state = selected?.StateName || this.form.state
      this.calculateTotals()
    },
    loadDistrictLocalBodies () {
      const district = String(this.form.district || '').trim()
      if (!district) return
      axios.get(`${this.$apiAdress}/api/address/local_bodies/${encodeURIComponent(district)}`, { timeout: 15000 })
        .then((response) => {
          const names = Array.isArray(response.data) ? response.data.filter(Boolean) : []
          this.localBodyOptions = [...new Set(names)].map((name) => ({
            local_body_name: name,
            local_body_type_name: this.form.local_body_type_name || ''
          }))
          if (this.form.local_body_name && !names.includes(this.form.local_body_name)) {
            this.form.local_body_name = ''
            this.selectedLocalBodyName = ''
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    onLocalBodySelect (value) {
      this.selectedLocalBodyName = value
      const selected = this.localBodyOptions.find((row) => row.local_body_name === value)
      if (!selected) {
        this.form.local_body_name = ''
        this.form.local_body_type_name = ''
        return
      }
      this.form.local_body_name = selected.local_body_name
      this.form.local_body_type_name = selected.local_body_type_name || ''
    },
    onPaymentMethodChange (value) {
      this.form.payment_method = value
      this.loadChargeEstimate()
    },
    addSelectedProduct () {
      const selected = this.productDraftItem
      const quantity = Number(this.productDraft.quantity || 0)
      if (!selected || quantity <= 0) return

      const existing = this.form.order_items.find((item) => String(item.item_id) === String(selected.value))
      if (existing) {
        existing.quantity = Number(existing.quantity || 0) + quantity
        existing.total = Number(existing.quantity || 0) * Number(existing.price || 0)
      } else {
        this.form.order_items.push({
          item_id: String(selected.value),
          name: selected.label || '',
          sku: selected.sku || '',
          shortcode: selected.shortcode || '',
          quantity,
          price: Number(selected.price || 0),
          total: Number(selected.price || 0) * quantity
        })
      }
      this.productDraft.item_id = ''
      this.productDraft.quantity = 1
      this.calculateTotals()
      this.loadChargeEstimate()
    },
    removeItem (index) {
      this.form.order_items.splice(index, 1)
      this.calculateTotals()
      this.loadChargeEstimate()
    },
    updateItemQuantity (index, quantity) {
      const item = this.form.order_items[index]
      item.quantity = Number(quantity || 0)
      this.calculateTotals()
      this.loadChargeEstimate()
    },
    onDiscountInput (event) {
      this.discountInput = Number(this.getInputValue(event) || 0)
      this.calculateTotals()
    },
    onShippingChargeInput (event) {
      this.form.shipping_charge = Number(this.getInputValue(event) || 0)
      this.calculateTotals()
    },
    calculateTotals () {
      let total = 0
      this.form.order_items.forEach((item) => {
        item.total = Number(item.quantity || 0) * Number(item.price || 0)
        total += Number(item.total || 0)
      })
      this.form.total = total

      if (this.discountMode === 'percent') {
        this.form.discount = (total * Number(this.discountInput || 0)) / 100
      } else {
        this.form.discount = Number(this.discountInput || 0)
      }

      const totalAfterDiscount = total - Number(this.form.discount || 0)
      const gstPercent = Number(this.form.gst_percentage || 5)
      // Product prices are MRP — already inclusive of GST. Extract the tax
      // component for reporting; do NOT add it to grand_total again.
      this.form.gst_amt = Number(((totalAfterDiscount * gstPercent) / (100 + gstPercent)).toFixed(2))

      const isKerala = String(this.form.state || '').trim().toLowerCase() === 'kerala'
      this.form.gst_state_code = isKerala ? 'KL' : ''
      if (isKerala) {
        this.form.cgst_amt = Number((this.form.gst_amt / 2).toFixed(2))
        this.form.sgst_amt = Number((this.form.gst_amt / 2).toFixed(2))
        this.form.igst_amt = 0
      } else {
        this.form.cgst_amt = 0
        this.form.sgst_amt = 0
        this.form.igst_amt = this.form.gst_amt
      }

      this.form.grand_total = Number(
        (totalAfterDiscount + Number(this.form.shipping_charge || 0) + Number(this.form.cod_fee || 0)).toFixed(2)
      )
    },
    loadChargeEstimate () {
      if (!this.isSold || !this.form.pincode || !this.form.order_items.length) {
        this.form.cod_fee = 0
        this.calculateTotals()
        return
      }
      axios.post(`${this.$apiAdress}/api/cal_cod_charges`, {
        organizationId: this.activeOrganisationId,
        pincode: this.form.pincode,
        quantity: this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        total: Number(this.form.total || 0),
        qtyAYS: 0,
        total_weight_grams: this.orderTotalWeightGrams,
        paymentMethod: this.form.payment_method,
        // Required: the courier decides the rate chart. Omitting it quoted
        // every field-sales order off the India Post distance chart, including
        // Professional Courier and Speed Post orders that price differently.
        shippingMethod: this.form.shipping_method
      })
        .then((response) => {
          this.form.shipping_charge = Number(response.data?.base_shipping ?? response.data?.cod_charge ?? 0)
          this.form.cod_fee = Number(response.data?.cod_fee || 0)
          this.calculateTotals()
        })
        .catch((error) => {
          console.log(error)
          this.form.cod_fee = 0
          this.calculateTotals()
        })
    },
    gpsString () {
      return this.locations
        .map((loc) => `${Number(loc.lat).toFixed(6)},${Number(loc.lng).toFixed(6)}`)
        .join(' | ')
    },
    submitVisit () {
      this.errorMessage = ''
      this.successMessage = ''
      if (!this.isFormReady) {
        this.errorMessage = this.readinessHint
        return
      }
      this.isSubmitting = true

      if (this.isSold) {
        this.createOrderThenVisit()
      } else {
        this.saveVisitRecord(null)
      }
    },
    createOrderThenVisit () {
      const paymentStatus = this.form.payment_method === 'cod' ? 'payment_pending' : 'paid'
      const payload = {
        contact_name: this.form.name,
        contact_phone: this.form.phone,
        contact_phone_2: this.form.phone,
        contact_address: this.form.address,
        contact_pincode: this.form.pincode,
        contact_postofficename: this.form.post_office_name,
        contact_district: this.form.district,
        contact_state: this.form.state || 'Kerala',
        local_body_name: this.form.local_body_name,
        local_body_type_name: this.form.local_body_type_name,
        ward_no: 0,
        country: 'India',
        shipping_type: 'domestic',
        order_type: 'direct_sale',
        // Total bottles = sum of line quantities, not the number of product
        // lines — feeds the "Bottles Sold" tiles, leaderboard and exports.
        item_count: this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        weight: 0,
        total: this.form.total,
        discount: this.form.discount,
        total_after_discount: this.totalAfterDiscount,
        shipping_charge: this.form.shipping_charge,
        cod_charge: this.form.cod_fee,
        total_shipping_charge: Number(this.form.shipping_charge || 0) + Number(this.form.cod_fee || 0),
        grand_total: this.form.grand_total,
        gst_percentage: this.form.gst_percentage,
        cgst_amt: this.form.cgst_amt,
        sgst_amt: this.form.sgst_amt,
        igst_amt: this.form.igst_amt,
        gst_amt: this.form.gst_amt,
        gst_state_code: this.form.gst_state_code,
        gst_stin: '32',
        payment_method: this.form.payment_method,
        payment_status: paymentStatus,
        payment_date: paymentStatus === 'paid' ? new Date().toISOString().slice(0, 10) : null,
        order_status: 'confirmed',
        shipping_method: this.form.shipping_method,
        shipping_status: 'for_print',
        order_items: this.form.order_items,
        is_call: 'no',
        comments: this.form.comments
      }

      axios.post(`${this.$apiAdress}/api/orders`, payload)
        .then((response) => {
          if (response?.data?.error) {
            this.errorMessage = typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error)
            this.isSubmitting = false
            return
          }
          const orderId = response?.data?.OrderId || response?.data?.order_id || null
          this.saveVisitRecord(orderId)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to create the order. The visit has not been saved.'
          this.isSubmitting = false
        })
    },
    saveVisitRecord (orderId) {
      const payload = {
        gps_coordinates: this.gpsString(),
        name: this.form.name,
        phone: this.form.phone,
        address: this.form.address,
        district: this.form.district,
        state: this.form.state,
        post_office_name: this.form.post_office_name,
        pincode: this.form.pincode ? Number(this.form.pincode) : null,
        status: this.form.visit_status,
        agro_type: this.form.agro_type,
        comments: this.form.comments
      }
      if (orderId) payload.order_id = orderId

      axios.post(`${this.$apiAdress}/api/field_visit`, payload)
        .then((response) => {
          if (response?.data?.error) {
            this.errorMessage = typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error)
            return
          }
          this.successMessage = orderId
            ? `Visit saved and order #${orderId} created.`
            : 'Visit saved successfully.'
          this.resetForm()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = orderId
            ? `Order #${orderId} was created but saving the visit failed. ${error?.response?.data?.message || ''}`.trim()
            : (error?.response?.data?.message || 'Unable to save the visit.')
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    resetForm () {
      this.locations = []
      this.locationError = ''
      this.postOfficeRecords = []
      this.postOfficeOptions = []
      this.localBodyOptions = []
      this.selectedLocalBodyName = ''
      this.productDraft = { item_id: '', quantity: 1 }
      this.discountMode = 'amount'
      this.discountInput = 0
      this.form = {
        name: '',
        phone: '',
        address: '',
        pincode: '',
        post_office_name: '',
        district: '',
        state: '',
        local_body_name: '',
        local_body_type_name: '',
        agro_type: '',
        visit_status: '',
        comments: '',
        payment_method: '',
        shipping_method: '',
        order_items: [],
        discount: 0,
        shipping_charge: 0,
        cod_fee: 0,
        total: 0,
        grand_total: 0,
        gst_percentage: 5,
        gst_amt: 0,
        gst_state_code: '',
        cgst_amt: 0,
        sgst_amt: 0,
        igst_amt: 0
      }
    }
  }
}
</script>

<style scoped>
.fs-page {
  padding: 0.25rem 0;
}

.fs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.fs-toolbar__title {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
}

.fs-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.fs-toolbar__subtitle {
  margin-top: 0.15rem;
  font-size: 0.78rem;
  color: #6b7280;
  max-width: 60ch;
}

.fs-toolbar__accent {
  width: 4px;
  height: 38px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.fs-toolbar__meta {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.fs-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.fs-grid {
  row-gap: 1rem;
}

.fs-main {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.fs-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.fs-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.fs-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  letter-spacing: -0.01em;
}

.fs-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fs-section__body {
  padding: 1rem;
}

.fs-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.fs-label__required {
  margin-left: 0.2rem;
  color: #dc2626;
  font-weight: 700;
}

.fs-label__status {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
}

.fs-label__status--warn {
  color: #b45309;
}

.fs-label-inline {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 0.15rem;
}

.fs-page .form-group {
  margin-bottom: 0.85rem;
}

.fs-page .form-control {
  font-size: 0.85rem;
  border-radius: 8px;
  min-height: 36px;
  height: 36px;
  border-color: #d8dde6;
}

.fs-page .form-control:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.fs-page .btn {
  border-radius: 8px;
  font-weight: 600;
}

/* Location bar */
.fs-location-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.fs-location-bar__hint {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #1f2a44;
  font-weight: 600;
}

.fs-location-bar__hint svg {
  color: #16a07a;
  flex-shrink: 0;
}

.fs-add-loc {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.95rem;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
}

.fs-add-loc:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}

.fs-add-loc:disabled {
  opacity: 0.7;
  cursor: progress;
}

.fs-add-loc:focus { outline: none; }
.fs-add-loc::-moz-focus-inner { border: 0; }
.fs-add-loc:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fs-add-loc__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: fs-spin 0.7s linear infinite;
}

@keyframes fs-spin { to { transform: rotate(360deg); } }

.fs-location-error {
  margin-top: 0.65rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.78rem;
}

.fs-location-list {
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.fs-location-list__row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 0.85rem;
  padding: 0.55rem 0.85rem;
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  background: #ffffff;
}

.fs-location-list__coords {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.fs-location-list__index {
  font-size: 0.7rem;
  font-weight: 700;
  color: #16a07a;
  background: #ecfdf5;
  border: 1px solid #c7ebda;
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
}

.fs-location-list__latlng {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.82rem;
  color: #1f2a44;
  font-weight: 600;
}

.fs-location-list__meta {
  font-size: 0.72rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.fs-location-list__map {
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
  text-decoration: none;
  padding: 0.25rem 0.55rem;
  border-radius: 6px;
  border: 1px solid #c7ebda;
  background: #f5fbf8;
}

.fs-location-list__map:hover {
  background: #ecfdf5;
}

.fs-location-list__map:focus { outline: none; }
.fs-location-list__map:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fs-location-list__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #e5e9f2;
  border-radius: 6px;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.fs-location-list__remove:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.fs-location-list__remove:focus { outline: none; }
.fs-location-list__remove::-moz-focus-inner { border: 0; }
.fs-location-list__remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}

/* Visit status grid */
.fs-status-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

@media (min-width: 768px) {
  .fs-status-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.fs-status-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.8rem 0.95rem;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, background 0.15s ease;
  position: relative;
}

.fs-status-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
  margin-bottom: 0.15rem;
}

.fs-status-card__label {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.fs-status-card__hint {
  font-size: 0.74rem;
  color: #6b7280;
  line-height: 1.3;
}

.fs-status-card:hover {
  border-color: #c7d2e0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
  transform: translateY(-1px);
}

.fs-status-card:focus { outline: none; }
.fs-status-card::-moz-focus-inner { border: 0; }
.fs-status-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fs-status-card--green.is-active {
  border-color: #16a07a;
  background: linear-gradient(135deg, #ecfdf5 0%, #f5fbf8 100%);
  box-shadow: 0 4px 14px rgba(14, 91, 70, 0.16);
}
.fs-status-card--green.is-active .fs-status-card__dot { background: #16a07a; box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.2); }
.fs-status-card--green.is-active .fs-status-card__label { color: #0e5b46; }

.fs-status-card--blue.is-active {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #f1f7ff 100%);
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.14);
}
.fs-status-card--blue.is-active .fs-status-card__dot { background: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2); }
.fs-status-card--blue.is-active .fs-status-card__label { color: #1d4ed8; }

.fs-status-card--amber.is-active {
  border-color: #d97706;
  background: linear-gradient(135deg, #fffbeb 0%, #fef7e0 100%);
  box-shadow: 0 4px 14px rgba(217, 119, 6, 0.14);
}
.fs-status-card--amber.is-active .fs-status-card__dot { background: #d97706; box-shadow: 0 0 0 4px rgba(217, 119, 6, 0.2); }
.fs-status-card--amber.is-active .fs-status-card__label { color: #92400e; }

.fs-status-card--violet.is-active {
  border-color: #7c3aed;
  background: linear-gradient(135deg, #f5f3ff 0%, #f8f5ff 100%);
  box-shadow: 0 4px 14px rgba(124, 58, 237, 0.14);
}
.fs-status-card--violet.is-active .fs-status-card__dot { background: #7c3aed; box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.2); }
.fs-status-card--violet.is-active .fs-status-card__label { color: #5b21b6; }

/* Product add bar */
.fs-product-add {
  display: grid;
  grid-template-columns: 1fr 110px 1fr auto;
  gap: 0.6rem;
  align-items: end;
  padding: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  margin-top: 0.25rem;
}

.fs-product-add__field {
  display: flex;
  flex-direction: column;
}

.fs-product-add__field--qty {
  max-width: 110px;
}

.fs-product-add__action {
  min-width: 100px;
}

.fs-product-add__action .btn {
  height: 36px;
}

/* Item list */
.fs-item-list {
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.fs-item-list__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px 110px auto;
  gap: 0.85rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.fs-item-list__row:hover {
  border-color: #c7d2e0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.fs-item-list__main { min-width: 0; }

.fs-item-list__name {
  font-weight: 600;
  color: #1f2a44;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fs-item-list__meta {
  font-size: 0.76rem;
  color: #6b7280;
  margin-top: 0.1rem;
}

.fs-item-list__qty {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.fs-item-list__total {
  display: flex;
  flex-direction: column;
}

.fs-item-list__total strong {
  color: #1f2a44;
  font-size: 0.95rem;
}

.fs-item-list__remove { white-space: nowrap; }

.fs-empty {
  text-align: center;
  padding: 1.25rem;
  color: #6b7280;
  font-size: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  margin-top: 0.85rem;
}

/* Discount */
.fs-discount-row {
  display: flex;
  gap: 0.5rem;
}

.fs-discount-row__mode {
  max-width: 80px;
}

/* Amount due */
.fs-amount-due {
  margin-top: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
}

.fs-amount-due__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.fs-amount-due__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.fs-amount-due__value {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
  line-height: 1.15;
}

.fs-amount-due__hint {
  font-size: 0.72rem;
  font-weight: 500;
  color: #6b7280;
  text-align: right;
}

/* Aside / sticky */
.fs-aside {
  display: flex;
  flex-direction: column;
}

.fs-sticky {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 1rem;
}

/* Summary */
.fs-summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px dashed #eef1f7;
  font-size: 0.85rem;
  color: #4b5563;
  gap: 0.85rem;
}

.fs-summary-line strong {
  color: #1f2a44;
  max-width: 60%;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fs-summary-divider {
  height: 1px;
  background: #e5e9f2;
  margin: 0.6rem 0 0.2rem;
}

.fs-summary-line--total {
  margin-top: 0.4rem;
  padding-top: 0.6rem;
  border-top: 2px solid #1f2a44;
  border-bottom: 0;
  font-size: 0.95rem;
}

.fs-summary-line--total strong {
  font-size: 1.1rem;
}

.fs-summary-line--note {
  margin-top: 0.15rem;
  padding: 0.25rem 0 0;
  border-bottom: 0;
  color: #6b7280;
  font-size: 0.74rem;
  font-style: italic;
}

.fs-summary-line--note strong {
  color: #6b7280;
  font-weight: 600;
  font-style: normal;
}

.fs-summary-actions {
  margin-top: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.fs-summary-actions__primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%) !important;
  border: none !important;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  font-weight: 700;
  height: 42px;
  letter-spacing: 0.01em;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.fs-summary-actions__primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}

.fs-summary-actions__primary:focus { outline: none; }
.fs-summary-actions__primary:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fs-summary-actions__hint {
  color: #6b7280;
  font-size: 0.74rem;
  text-align: center;
}

@media (max-width: 991.98px) {
  .fs-sticky {
    position: static;
  }
}
</style>
